import React, { useState, useId } from "react";
import { Controller } from "react-hook-form";
import ClearIcon from "@mui/icons-material/Clear";
import {
  ErrorMessage,
  IconWrapper,
  InputFieldWrapper,
  InputWrapper,
  StyledInput,
} from "./styles";
import { IconButton } from "../../IconButton";
import { FormInputProps } from "./types";

const formatPercentage = (value: number | string) => {
  if (value === "" || value === null || Number.isNaN(Number(value))) return "";
  const formatted = Number(value).toFixed(3);
  return `${Number.parseFloat(formatted)} %`;
};

const parsePercentage = (value: string) => {
  if (!value) return "";
  // Keep only numbers and decimal separator, replace comma with dot
  let parsed = value.replaceAll(/[^0-9.,]/g, "").replaceAll(",", ".");
  // Restrict to 3 decimals
  const parts = parsed.split(".");
  if (parts.length > 1) {
    parsed = `${parts[0]}.${parts[1].slice(0, 3)}`;
  }
  return parsed;
};

export const FormPercentageInput = ({
  name,
  control,
  rules,
  disabled = false,
  placeholder = "",
  handleClear = false,
  id,
  required = false,
}: Omit<FormInputProps, "multiline" | "type">) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const [displayValue, setDisplayValue] = useState(
          value !== undefined && value !== null && value !== ""
            ? String(value)
            : "",
        );

        return (
          <InputWrapper>
            <InputFieldWrapper>
              <StyledInput
                id={inputId}
                type="text"
                $error={!!error}
                disabled={disabled}
                required={required}
                aria-required={required || undefined}
                aria-invalid={!!error}
                aria-describedby={error ? errorId : undefined}
                value={displayValue}
                placeholder={placeholder}
                onChange={(e) => {
                  const raw = parsePercentage(e.target.value);
                  setDisplayValue(raw);
                  onChange(raw === "" ? "" : Number(raw));
                }}
                onBlur={() => {
                  if (displayValue !== "") {
                    setDisplayValue(formatPercentage(displayValue));
                  }
                }}
                onFocus={() => {
                  if (displayValue !== "") {
                    setDisplayValue(String(value));
                  }
                }}
              />
              {!disabled &&
                handleClear &&
                displayValue &&
                displayValue.length > 0 && (
                  <IconWrapper>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDisplayValue("");
                        onChange("");
                      }}
                      aria-label="Clear value"
                    >
                      <ClearIcon />
                    </IconButton>
                  </IconWrapper>
                )}
              {error && (
                <ErrorMessage id={errorId} role="alert">
                  {error.message}
                </ErrorMessage>
              )}
            </InputFieldWrapper>
          </InputWrapper>
        );
      }}
    />
  );
};
